import { createAsyncThunk } from "@reduxjs/toolkit";
import customFetch from "../../util/config/axiosConfig";
import { getDataUserId } from "../../util/manageAuth";
import {
  Customer,
  CustomerSearchParams,
  CustomerSearchResponse,
} from "../../types/CustomerType";

export const addCustomer = createAsyncThunk<
  Customer,
  Customer,
  { rejectValue: string }
>(
  "customer/addCustomer",
  async (customerData, thunkAPI) => {
    try {
      const response = await customFetch.post(
        `/customers/user/${getDataUserId()}`,
        customerData
      );

      let payload = response.data;
      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      return payload.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to add customer"
      );
    }
  }
);

// NEW: Fetch single customer by ID
export const fetchCustomerById = createAsyncThunk<
  Customer,
  string | number,
  { rejectValue: string }
>(
  "customer/fetchCustomerById",
  async (customerId, thunkAPI) => {
    try {
      const response = await customFetch.get(`/customers/${customerId}`);

      let payload = response.data;
      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      return payload.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to fetch customer"
      );
    }
  }
);

export const updateCustomer = createAsyncThunk<
  Customer,
  Customer & { id: string | number },
  { rejectValue: string }
>(
  "customer/updateCustomer",
  async (customerData, thunkAPI) => {
    try {
      const response = await customFetch.post(
        `/customers/user/${getDataUserId()}`,
        customerData  // ← send full object with id included
      );

      let payload = response.data;
      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      return payload.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to update customer"
      );
    }
  }
);

// Delete customer by ID
export const deleteCustomer = createAsyncThunk<
  void,
  string | number,
  { rejectValue: string }
>(
  "customer/deleteCustomer",
  async (customerId, thunkAPI) => {
    try {
      await customFetch.delete(`/customers/${customerId}`);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to delete customer"
      );
    }
  }
);

export const fetchCustomers = createAsyncThunk<
  CustomerSearchResponse,
  CustomerSearchParams | undefined,
  { rejectValue: string }
>(
  "customer/fetchCustomers",
  async (params, thunkAPI) => {
    try {
      const userId = getDataUserId();
      if (!userId) throw new Error("User not authenticated");

      const page = params?.page ?? 0;
      const size = params?.size ?? 50;

      const body = {
        userId,
        fullName: params?.filters?.fullName || "",
        mobile: params?.filters?.mobile || "",
        email: params?.filters?.email || "",
        city: params?.filters?.city || "",
        status: params?.filters?.status ?? null,
      };

      const response = await customFetch.post(
        `/customers/search?page=${page}&size=${size}`,
        body
      );

      let payload = response.data;
      if (typeof payload?.data === "string") {
        payload.data = JSON.parse(payload.data);
      }

      return {
        results: payload.data.results,
        page: payload.data.page,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to fetch customers"
      );
    }
  }
);